/**
 * The edit walk (Phase 6 of docs/plans/setup-walk.md): what OS looks at on a
 * built workspace before it adds or removes one field.
 *
 * Pure: the phone bundles it.
 */
import type { PostingMap, RoleSource } from '../ledger/patterns';

/**
 * The kinds of value a field's job is judged in. A phone kept as text and a
 * phone kept as a phone do the same job; a number and an amount may. A field
 * worked out, totalled or numbered by itself is never offered in a typed
 * field's place, so those types sit in no family.
 */
const FAMILY: Readonly<Record<string, string>> = {
  text: 'text',
  long_text: 'text',
  phone: 'text',
  number: 'number',
  money: 'number',
  quantity: 'number',
  date: 'date',
  datetime: 'date',
  select: 'choice',
  multi_select: 'choice',
  reference: 'pick',
  boolean: 'boolean',
  attachment: 'attachment',
  location: 'location',
};

/** Words that say the same job (W7: words alone are a hint, so the set stays small). */
const SYNONYMS: ReadonlyArray<readonly string[]> = [
  ['phone', 'mobile', 'cell', 'contact', 'whatsapp'],
  ['price', 'rate', 'cost'],
  ['qty', 'quantity'],
  ['note', 'remark', 'comment'],
  ['email', 'mail'],
];
const CANONICAL = new Map(SYNONYMS.flatMap((group) => group.map((word) => [word, group[0]!] as const)));
/** Words that name no job of their own: "Mobile number" is the mobile. */
const FILLER = new Set(['no', 'number', 'num', 'the', 'of', 'a', 'an', 'per', 'each', 'unit']);

/** A label's job words: case-folded, plurals and filler dropped, each said by its group's first word. */
const jobWords = (label: string): string => {
  const words = label
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word !== '' && !FILLER.has(word))
    .map((word) => (word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word))
    .map((word) => CANONICAL.get(word) ?? word);
  return [...new Set(words)].sort().join(' ');
};

type JobField = { readonly id: string; readonly label: string; readonly type: string; readonly enabled?: boolean };

/**
 * 6.1: a field on the record that likely does the job a new one would ("Phone"
 * beside a new "Mobile"): the same kind of value and the same job words. Null
 * when none does. A label already taken is the planner's (LABEL_TAKEN), not
 * this note's.
 */
export const sameJobField = <F extends JobField>(label: string, type: string, fields: readonly F[]): F | null => {
  const family = FAMILY[type];
  const words = jobWords(label);
  if (family === undefined || words === '') return null;
  const taken = label.trim().toLowerCase();
  return (
    fields.find(
      (field) =>
        field.enabled !== false &&
        FAMILY[field.type] === family &&
        field.label.trim().toLowerCase() !== taken &&
        jobWords(field.label) === words,
    ) ?? null
  );
};

const sourceFields = (source: RoleSource | undefined, into: Set<string>): void => {
  if (source === undefined || !('field' in source)) return;
  into.add(source.field);
  for (const inner of [...Object.values(source.options ?? {}), ...Object.values(source.each ?? {})]) sourceFields(inner, into);
};

/**
 * 6.2: every field a record's money rule reads (its roles, the choice that
 * picks the pattern, the quantity it moves, the dimensions it keeps), so an
 * archive can say the money reads it.
 */
export const moneyRuleFields = (map: PostingMap): ReadonlySet<string> => {
  const into = new Set<string>();
  for (const source of Object.values(map.roles ?? {})) sourceFields(source, into);
  const quantities = [map.quantity];
  if (map.by_choice !== undefined) {
    into.add(map.by_choice.field);
    for (const option of Object.values(map.by_choice.options)) {
      for (const source of Object.values(option.roles ?? {})) sourceFields(source, into);
      quantities.push(option.quantity ?? undefined);
    }
  }
  for (const quantity of quantities) {
    if (quantity === undefined) continue;
    for (const field of [quantity.item, quantity.qty, quantity.unit, quantity.lines]) if (field !== undefined) into.add(field);
  }
  for (const field of map.dimensions ?? []) into.add(field);
  return into;
};
